import { isNullOrUndefined } from 'util';
import { AppTextService } from '@fi-shared/app-text';
import { IChecklist2ReportBenchUnitData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { fiTextToHtml, isDefined } from '@fi-shared/utils';
import { IChecklistBenchmarkViewData } from '@fi-shared/checklist/fi-checklist2-report/components/checklist-history-report/unit-performance-benchmark/checklists-benchmark/checklists-benchmark.component';
import { ObjectTypes } from '@fi-shared/types';
import { BorderRadius } from 'chart.js';

export const checklistUnitBenchmarkChartExternalTooltipHandler = (
    context,
    checklistBenchmarkData: IChecklistBenchmarkViewData,
    appTextService: AppTextService,
    objectType: ObjectTypes,
    checklistName: string,
) => {
    const { chart, tooltip } = context;
    const customTooltipEl: HTMLElement = chart.canvas.parentNode.querySelector('#chart-tooltip');
    // Hide if no tooltip
    if (tooltip.opacity === 0) {
        customTooltipEl.style.opacity = '0';
        return;
    }

    // Set Text
    if (tooltip.body) {
        const currentDataForGraph: IChecklist2ReportBenchUnitData =
            checklistBenchmarkData.Units[tooltip.dataPoints[0].dataIndex];

        const checkpointsLabel: string = appTextService
            .get(
                currentDataForGraph.TotalCheckpointsCount > 1
                    ? 'checklist.checkpoints'
                    : 'checklist.names_of_checkpoints',
            )
            .toLowerCase();

        const checkpointsCounterLabel: string = checklistBenchmarkData.CheckpointsCount + ' ' + checkpointsLabel;
        const iterationsLabel: string =
            currentDataForGraph.IterationsCount +
            ' ' +
            appTextService.get(
                currentDataForGraph.IterationsCount > 1 ? 'common.times_counter' : 'common.time_counter',
            );

        let tooltipResultText: string;
        if (objectType === ObjectTypes.Checkpoint2) {
            tooltipResultText = appTextService.get('checklist.unit_benchmark_chart_tooltip_heading_checkpoints', {
                '{checkpoint_name}': fiTextToHtml(checklistBenchmarkData.ObjectName),
                '{checklist_name}': checklistName,
                '{iterations_label}': iterationsLabel,
                '{unit_name}': fiTextToHtml(currentDataForGraph.Name),
            });
        } else if (objectType === ObjectTypes.CheckpointGroup) {
            tooltipResultText = appTextService.get('checklist.unit_benchmark_chart_tooltip_heading_checkpoint_groups', {
                '{section_name}': fiTextToHtml(checklistBenchmarkData.ObjectName),
                '{checklist_name}': checklistName,
                '{checkpoints_label}': checkpointsCounterLabel,
                '{iterations_label}': iterationsLabel,
                '{unit_name}': fiTextToHtml(currentDataForGraph.Name),
            });
        } else if (objectType === ObjectTypes.Checklist2) {
            tooltipResultText = appTextService.get('checklist.unit_benchmark_chart_tooltip_heading', {
                '{checklist_name}': fiTextToHtml(checklistBenchmarkData.ObjectName),
                '{checkpoints_label}': checkpointsCounterLabel,
                '{iterations_label}': iterationsLabel,
                '{unit_name}': fiTextToHtml(currentDataForGraph.Name),
            });
        } else {
            throw new Error(`checklistUnitBenchmarkChartExternalTooltipHandler: unknown object type ${objectType}`);
        }

        customTooltipEl.querySelector('#unit-benchmark-tooltip-heading').innerHTML = tooltipResultText;

        const deviationsPercent: number = !currentDataForGraph.TotalCheckpointsCount
            ? 0
            : getDeviationCheckpointsPercentage(currentDataForGraph);
        const deviationsNumValue: string = `<span class="light-color bold">(${currentDataForGraph.NotOkCheckpointsCount}/${currentDataForGraph.TotalCheckpointsCount})</span> <span class="light-color">${checkpointsLabel}</span>`;
        customTooltipEl.querySelector('#checklist-deviations-counter').innerHTML =
            `${deviationsPercent}% ${deviationsNumValue}`;

        const completedPercent: number = !currentDataForGraph.TotalCheckpointsCount
            ? 0
            : getOkCheckpointsPercentage(currentDataForGraph);
        const completedNumValue: string = `<span class="light-color bold">(${currentDataForGraph.OkCheckpointsCount}/${currentDataForGraph.TotalCheckpointsCount})</span> <span class="light-color">${checkpointsLabel}</span>`;
        customTooltipEl.querySelector('#checklist-ok-counter').innerHTML = `${completedPercent}% ${completedNumValue}`;
    }

    const tooltipLeftBottomOffset: {
        left: string;
        bottom: string;
    } = getTooltipLeftBottomOffset(customTooltipEl, context);
    customTooltipEl.style.left = tooltipLeftBottomOffset.left;
    customTooltipEl.style.bottom = tooltipLeftBottomOffset.bottom;
    customTooltipEl.style.opacity = '1';
};

const getTooltipLeftBottomOffset = (customTooltipEl: HTMLElement, context): { left: string; bottom: string } => {
    let leftOffsetResultPx: number = 0;
    let bottomOffsetResultPx: number = 0;

    const mainWrapperEl: Element = document
        .getElementsByClassName('fi-history-checklist-details-unit-performance-benchmark')
        .item(0);
    if (!isDefined(mainWrapperEl)) return;

    const { chart, tooltip } = context;
    const { offsetLeft: positionX } = chart.canvas;

    const tooltipElRect: DOMRect = customTooltipEl.getBoundingClientRect();
    const mainRect: DOMRect = mainWrapperEl.getBoundingClientRect();
    const canvasRect: DOMRect = chart.canvas.getBoundingClientRect();

    // calc left offset section
    const barWidth = chart.getDatasetMeta(0).data[0].width;
    const tooltipSideOffsetPx: number = 10;

    const tooltipRightAlignmentOffset: number =
        positionX + tooltip.caretX + tooltipElRect.width / 2 + barWidth / 2 + tooltipSideOffsetPx;
    const tooltipLeftAlignmentOffset: number =
        positionX + tooltip.caretX - tooltipElRect.width / 2 - barWidth / 2 - tooltipSideOffsetPx;

    const tooltipElLeftOffset: number =
        canvasRect.left + positionX + tooltip.caretX + barWidth + tooltipSideOffsetPx - mainRect.left;
    const isTooltipOverflowsXAxis: boolean = mainRect.width - tooltipElLeftOffset - tooltipElRect.width <= 0;

    leftOffsetResultPx = isTooltipOverflowsXAxis ? tooltipLeftAlignmentOffset : tooltipRightAlignmentOffset;

    // calc bottom offset section
    const { height: barHeight, y: barY } = chart.getDatasetMeta(0).data[tooltip.dataPoints[0].dataIndex];
    const barMiddle: number = Number.isNaN(barHeight) || barHeight === 0 ? 0 : barHeight / 2;

    const yAxisLabelsContainerHeight: number = canvasRect.height - barY - barHeight;

    const bottomOffset: number = barMiddle + yAxisLabelsContainerHeight - tooltipElRect.height / 2;
    const minOffsetBottom: number = canvasRect.bottom - mainRect.bottom;

    bottomOffsetResultPx = Math.max(bottomOffset, minOffsetBottom);

    return {
        left: `${leftOffsetResultPx}px`,
        bottom: `${bottomOffsetResultPx}px`,
    };
};

export const getOkCheckpointsBackgroundFn = (context): CanvasGradient => {
    if (!context.chart.chartArea) return;

    const {
        ctx,
        chartArea: { top, bottom },
    } = context.chart;
    const gradientBackgroundColor = ctx.createLinearGradient(0, top, 0, bottom);
    gradientBackgroundColor.addColorStop(0, '#99FFA3');
    gradientBackgroundColor.addColorStop(1, '#13CA25');
    return gradientBackgroundColor;
};

export const getDeviationCheckpointsBackgroundFn = (context): CanvasGradient => {
    if (!context.chart.chartArea) return;

    const {
        ctx,
        chartArea: { top, bottom },
    } = context.chart;
    const gradientBackgroundColor = ctx.createLinearGradient(0, top, 0, bottom);
    gradientBackgroundColor.addColorStop(0, '#FF897D');
    gradientBackgroundColor.addColorStop(1, '#DE3730');
    return gradientBackgroundColor;
};

export const getOkCheckpointsBorderRadiusFn = (ctx): number | BorderRadius => {
    const dataIndex: number = ctx.dataIndex;
    const val: number = ctx.chart.data.datasets[1].data[dataIndex];
    if (isNullOrUndefined(val) || val === 0)
        return {
            topLeft: UnitBenchmarkChartBarBorderRadius,
            topRight: UnitBenchmarkChartBarBorderRadius,
            bottomLeft: 0,
            bottomRight: 0,
        };

    return 0;
};

export const getUnitBenchmarkChartLabels = (units: IChecklist2ReportBenchUnitData[]) => {
    return units.map((unit: IChecklist2ReportBenchUnitData) => unit.Name);
};

export const getOkCheckpointsPercentage = (unit: IChecklist2ReportBenchUnitData) => {
    return Math.round((unit.OkCheckpointsCount * 100) / unit.TotalCheckpointsCount);
};

export const getDeviationCheckpointsPercentage = (unit: IChecklist2ReportBenchUnitData) => {
    return Math.round((unit.NotOkCheckpointsCount * 100) / unit.TotalCheckpointsCount);
};

export const UnitBenchmarkChartBarBorderRadius: number = 20;
export const UnitBenchmarkChartMaxBarThickness: number = 70;
